import { useState, useEffect, useCallback, useRef } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  OptionProps,
  MultiValueProps,
  MenuProps,
  ActionMeta,
} from "react-select";
import { Option } from "../../interface/Option";
import { NewNetworkAxiosInstance } from "../../Network/NewNetworkAxiosInstance";

interface NetworkOption extends Option {
  is_active?: boolean | number | string;
  Is_active?: boolean | number | string;
  status?: string;
  Status?: string;
  __inactive?: boolean;
}

interface ListOfNewNetworkNameProps {
  onChange: (selected: NetworkOption[]) => void;
  clear?: boolean;
  defaultValue?: NetworkOption[];
  qaIdPrefix?: string;
}

const DEFAULT_QA_PREFIX = "list-of-new-network-name";
const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown").trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;
const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return { id, "data-testid": id, "data-qa": id };
};

// Never infer inactive from absence of a status field.
const explicitInactive = (item: any): boolean => {
  if (item?.__inactive === true) return true;
  const value = item?.is_active ?? item?.Is_active ?? item?.isActive ?? item?.status ?? item?.Status;
  return value === false || value === 0 || value === "0" ||
    (typeof value === "string" && ["inactive", "disabled", "false"].includes(value.trim().toLowerCase()));
};
const networkKey = (item: any) => String(item?.value ?? item?.id ?? item?.dbId ?? "");
const mergeStatus = (item: NetworkOption, source?: NetworkOption): NetworkOption => ({
  ...item,
  is_active: source?.is_active ?? item.is_active,
  status: source?.status ?? source?.Status ?? item.status ?? item.Status,
  __inactive: explicitInactive(item) || (!!source && explicitInactive(source)),
});

// Fetch current statuses on each mount; a module-level cache makes recent changes stale.
const fetchAllNetworks = async (): Promise<NetworkOption[]> => {
  const response = await NewNetworkAxiosInstance.get("getNetwork");
  const rawResult = response.data?.result ?? response.data?.data ?? [];
  const data: any[] = Array.isArray(rawResult)
    ? rawResult
    : Array.isArray(rawResult?.data) ? rawResult.data : [];

  return data.map((item: any): NetworkOption | null => {
    const networkId = item?.id ?? item?.network_id ?? item?.Network_id;
    const networkName = String(
      item?.name ?? item?.network_name ?? item?.Network_name ?? item?.networkName ?? ""
    ).trim();
    if (networkId == null || !networkName) return null;
    return {
      value: String(networkId),
      label: `(${networkId}) ${networkName}`,
      is_active: item?.is_active ?? item?.Is_active ?? item?.isActive,
      status: item?.status ?? item?.Status,
      __inactive: explicitInactive(item),
    };
  }).filter((item: NetworkOption | null): item is NetworkOption => item !== null);
};

const ListOfNewNetworkName = ({
  onChange, clear, defaultValue, qaIdPrefix = DEFAULT_QA_PREFIX,
}: ListOfNewNetworkNameProps) => {
  const [allOptions, setAllOptions] = useState<NetworkOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<NetworkOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const selectedRef = useRef<NetworkOption[]>([]);
  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const applySelected = useCallback((items: NetworkOption[]) => {
    selectedRef.current = items;
    setSelectedOptions(items);
  }, []);

  const loadAllOptions = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await fetchAllNetworks();
      setAllOptions(fresh);
      const sourceById = new Map(fresh.map(item => [networkKey(item), item]));
      // Existing related networks are retained even when the dropdown filters them.
      applySelected(selectedRef.current.map(item => mergeStatus(item, sourceById.get(networkKey(item)))));
    } catch (error) {
      console.error("Error loading Network options:", error);
      setAllOptions([]);
    } finally {
      setLoading(false);
    }
  }, [applySelected]);

  useEffect(() => { void loadAllOptions(); }, [loadAllOptions]);

  useEffect(() => {
    if (clear) applySelected([]); // Explicit form reset, not user-triggered deletion.
  }, [clear, applySelected]);

  useEffect(() => {
    if (defaultValue !== undefined) {
      const sourceById = new Map(allOptions.map(item => [networkKey(item), item]));
      applySelected((defaultValue || []).map(item => mergeStatus(item, sourceById.get(networkKey(item)))));
    }
    // defaultValue is owned by the parent. A new reference signals a new Edit record.
  }, [defaultValue, applySelected]);

  // Enrich existing choices when source statuses arrive AFTER defaultValue.
  useEffect(() => {
    if (!allOptions.length) return;
    const sourceById = new Map(allOptions.map(item => [networkKey(item), item]));
    const current = selectedRef.current;
    const next = current.map(item => mergeStatus(item, sourceById.get(networkKey(item))));
    if (next.some((item, i) => item.__inactive !== current[i]?.__inactive)) applySelected(next);
  }, [allOptions, applySelected]);

  const handleChange = (selected: MultiValue<NetworkOption>, _action: ActionMeta<NetworkOption>) => {
    const previous = selectedRef.current;
    const incoming = [...selected];
    const next = incoming.map(item =>
      mergeStatus(item, previous.find(p => networkKey(p) === networkKey(item)))
    );
    // React-select backspace, Clear All and remove operations can't drop locked references.
    previous.forEach(item => {
      if (explicitInactive(item) && !next.some(n => networkKey(n) === networkKey(item))) {
        next.push(item);
      }
    });
    applySelected(next);
    onChange(next);
  };

  const options = allOptions.filter(item => {
    if (explicitInactive(item)) return false;
    const query = inputValue.trim().toLowerCase();
    if (!query) return true;
    const name = String(item.label ?? "").replace(/^\([^)]*\)\s*/, "");
    return name.toLowerCase().includes(query);
  });

  const customStyles: StylesConfig<NetworkOption, true> = {
    multiValue: (base, { data }) => ({
      ...base,
      backgroundColor: explicitInactive(data) ? "#d1d5db" : "#007BFF",
      border: explicitInactive(data) ? "1px solid #94a3b8" : "1px solid transparent",
      borderRadius: "5px", opacity: 1,
    }),
    multiValueLabel: (base, { data }) => ({
      ...base, color: explicitInactive(data) ? "#334155" : "#FFFFFF",
      fontWeight: explicitInactive(data) ? 600 : base.fontWeight,
    }),
    multiValueRemove: (base, { data }) => ({
      ...base,
      display: explicitInactive(data) ? "none" : base.display,
      color: "#FFFFFF",
      ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
    }),
    option: (base, { isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      cursor: "pointer",
      ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
    }),
  };

  const Menu = (props: MenuProps<NetworkOption, true>) => (
    <components.Menu {...props} innerProps={{ ...(props.innerProps || {}), ...qaAttrs(qaIdPrefix, "menu") }} />
  );
  const OptionComponent = (props: OptionProps<NetworkOption, true>) => (
    <components.Option
      {...props}
      innerProps={{
        ...props.innerProps,
        ...qaAttrs(qaIdPrefix, `option-${qaSafe(String(props.data.value))}`),
        "data-option-label": props.data.label,
        "data-option-value": props.data.value,
      } as any}
    />
  );
  const MultiValueComponent = (props: MultiValueProps<NetworkOption, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `selected-${qaSafe(String(props.data.value))}`)}
      data-selected-label={props.data.label}
      data-selected-value={props.data.value}
      data-inactive={explicitInactive(props.data) ? "true" : "false"}
    >
      <components.MultiValue {...props} />
    </div>
  );
  const MultiValueRemove = (props: any) =>
    explicitInactive(props.data) ? null : <components.MultiValueRemove {...props} />;

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select<NetworkOption, true>
        menuPosition="absolute"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        captureMenuScroll={true}
        isMulti
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        className={selectQaId}
        aria-label="Network selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={(value, meta) => {
          if (meta.action === "input-change") setInputValue(value);
          return value;
        }}
        placeholder="Type to search or select from list..."
        noOptionsMessage={() => loading ? "Loading..." : "No networks found"}
        styles={{
          ...customStyles,
          menu: base => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
          menuList: base => ({
            ...base, maxHeight: 280, overflowY: "auto",
            pointerEvents: "auto", WebkitOverflowScrolling: "touch",
          }),
        }}
        components={{ Menu, Option: OptionComponent, MultiValue: MultiValueComponent, MultiValueRemove }}
      />
      <span {...qaAttrs(qaIdPrefix, "loading-state")} data-loading={loading ? "true" : "false"} style={{ display: "none" }}>
        {loading ? "Loading" : "Idle"}
      </span>
      <span {...qaAttrs(qaIdPrefix, "selected-count")} data-count={selectedOptions.length} style={{ display: "none" }}>
        {selectedOptions.length}
      </span>
      <span {...qaAttrs(qaIdPrefix, "options-count")} data-count={options.length} style={{ display: "none" }}>
        {options.length}
      </span>
    </div>
  );
};

export default ListOfNewNetworkName;
